import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import {
  type BlocksModelElementType,
  type BundleConfig,
  type BundleConfigItem,
  type BundleConfigOption,
  type FlavorsSelection,
  type LastSelectedElement,
  type StockStatusElements,
} from "@jti/fe-common";
import type { MagentoBrandConsumables, MagentoStockStatus } from "@jti/magento-types";
import { MultibrandMultipleFlavorsSelector } from "@jti/ui/components/MultibrandMultipleFlavorsSelector";
import { MultipleFlavorsSelector } from "@jti/ui/components/MultipleFlavorsSelector";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { type MultipacksQuantity } from "@product-details/common.types";
import {
  getBundleConfig,
  getBundleOptions,
  getInitialSticks,
  getMixedConsumableFlavors,
  getMultipleFlavorsWithBrands,
  getQtyForMultipack,
  isVisibleByStockStatus,
  setCurrentConfig,
} from "@product-details/utils";

import ProductBundlesBlock from "./ProductBundlesBlock";
import ProductBundlesStickBlock from "./ProductBundlesStickBlock";

import "./ProductBundles.scss";

export type BlocksModelType = Record<string, BlocksModelElementType>;

const getElement = (element: BundleConfigOption, stockStatus?: MagentoStockStatus) => {
  return {
    product: element,
    data: {
      value: element.id,
      quantity: 1,
      isInStock: element.isInStock ?? true,
      stockStatus,
    },
  };
};

const getCurrentModel = (blocks: BundleConfigItem[], stockStatusElements?: StockStatusElements) =>
  blocks.reduce((prev: BlocksModelType, block: BundleConfigItem) => {
    const productObject = block.allOptions.find((option) => option.isDefault);

    if (!productObject) {
      return prev;
    }

    return {
      ...prev,
      [block.optionId]: getElement(productObject, stockStatusElements?.[productObject.id]),
    };
  }, {});

type ProductBundlesProps = {
  config: BundleConfig;
  brandMatrix?: MagentoBrandConsumables[];
  isStockLoading: boolean;
  showValidation: boolean;
  stockStatusElements: StockStatusElements;
  multipacksQuantities: MultipacksQuantity[];
  onSet: (data: any) => void;
  setLastSelectedElement: (data: LastSelectedElement) => void;
  dispatchViewItem?: () => void;
  children?: ReactNode;
};

const ProductBundles = ({
  config,
  brandMatrix,
  isStockLoading,
  showValidation,
  stockStatusElements,
  multipacksQuantities,
  onSet,
  setLastSelectedElement,
  dispatchViewItem,
  children,
}: ProductBundlesProps) => {
  const {
    displayOutOfStockBundleOptions,
    displayAvailableInStoresBundleOptions,
    drawerFlavourSelectorForBundles = false,
  } = useConfig();
  const { __ } = useTranslations();
  const [selectedElements, setSelectedElements] = useState<BlocksModelType>({});
  const { blocks, sticks } = getBundleConfig(config);

  const onChange = (optionId: number) => (data: any) => {
    if (!data?.value) {
      return;
    }

    const newSelectedElement = {
      [optionId]: getElement(
        {
          ...data.additionalData,
          isInStock: data.isInStock,
        },
        data.stockStatus
      ),
    };

    const lastSelectedElement: LastSelectedElement = {
      optionId,
      productId: newSelectedElement[optionId].product.id,
      type: "select",
    };
    setLastSelectedElement(lastSelectedElement);

    setSelectedElements({
      ...selectedElements,
      ...newSelectedElement,
    });

    dispatchViewItem?.();
  };

  const onChangeStick = (optionId: number, item: BundleConfigItem) => (data: any) => {
    const newSelectedElement = {
      [optionId]: {
        data,
        product: item,
      },
    };

    const lastSelectedElement: LastSelectedElement = {
      optionId,
      type: "multi",
      quantities: data.quantities,
    };

    setLastSelectedElement(lastSelectedElement);
    setSelectedElements({
      ...selectedElements,
      ...newSelectedElement,
    });
  };

  const handleStickSelection = useCallback(
    (optionId: number, item: BundleConfigItem) => (data: FlavorsSelection) => {
      const quantities = Object.entries(data).map(([selection_id, qty]) => ({
        selection_id,
        qty,
      }));

      const newSelectedElement = {
        [optionId]: {
          data: {
            value: Object.keys(data),
            quantities: quantities,
          },
          product: item,
        },
      };

      const lastSelectedElement: LastSelectedElement = {
        optionId,
        type: "multi",
        quantities: quantities,
      };

      setLastSelectedElement(lastSelectedElement);
      setSelectedElements({
        ...selectedElements,
        ...newSelectedElement,
      });
    },
    [selectedElements, setLastSelectedElement]
  );

  useEffect(() => {
    setSelectedElements((prev) => ({ ...prev, ...getCurrentModel(blocks, stockStatusElements) }));
  }, [multipacksQuantities]);

  useEffect(() => {
    onSet(setCurrentConfig(selectedElements));
  }, [selectedElements]);

  const getItemsOptionsList = (item: BundleConfigItem) => {
    if (!Array.isArray(item.allOptions)) {
      return [];
    }
    return item.allOptions.filter((option) =>
      isVisibleByStockStatus(stockStatusElements[option?.id], {
        displayOutOfStock: displayOutOfStockBundleOptions ?? false,
        displayAvailableInStores: displayAvailableInStoresBundleOptions ?? false,
      })
    );
  };

  return (
    <>
      {blocks.map((item: BundleConfigItem) => (
        <ProductBundlesBlock
          isLoading={isStockLoading}
          title={item.title}
          currentProduct={selectedElements[item.optionId]?.product || null}
          showValidation={showValidation}
          key={item.title}
          onChange={onChange(item.optionId)}
          options={getBundleOptions(getItemsOptionsList(item), stockStatusElements)}
          stockStatusElements={stockStatusElements}
        />
      ))}

      {children}

      {sticks.map((item: BundleConfigItem) => {
        const numberOfSticks = getQtyForMultipack(multipacksQuantities, item.optionId);
        const options = getBundleOptions(getItemsOptionsList(item), stockStatusElements);
        const flavors = options.map(getMixedConsumableFlavors);
        const flavorsByBrand = getMultipleFlavorsWithBrands(flavors, brandMatrix);

        if (!drawerFlavourSelectorForBundles) {
          return (
            <ProductBundlesStickBlock
              key={item.sku}
              isLoading={isStockLoading}
              title={item.title}
              listAriaLabel={__("productdetails.sticks.selectionListLabel")}
              sticksCount={numberOfSticks}
              showValidation={showValidation}
              options={options}
              stockStatusElements={stockStatusElements}
              onChange={onChangeStick(item.optionId, item)}
            />
          );
        }

        if (!flavorsByBrand) {
          return (
            <MultipleFlavorsSelector
              key={item.sku}
              limit={numberOfSticks}
              shouldValidate={showValidation}
              items={flavors}
              stockStatusElements={stockStatusElements}
              onChange={handleStickSelection(item.optionId, item)}
              defaultFlavorsSelection={getInitialSticks(options, numberOfSticks, stockStatusElements)}
            />
          );
        }

        return (
          <MultibrandMultipleFlavorsSelector
            key={item.sku}
            title={item.title}
            limit={numberOfSticks}
            showValidation={showValidation}
            brands={flavorsByBrand}
            stockStatusElements={stockStatusElements}
            onChange={handleStickSelection(item.optionId, item)}
            defaultFlavorsSelection={getInitialSticks(options, numberOfSticks, stockStatusElements)}
          />
        );
      })}
    </>
  );
};

export default ProductBundles;
